import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, ShieldCheck, LayoutDashboard, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/courses", label: "Courses" },
  { to: "/verify", label: "Verify Certificate" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/policies/terms", label: "Policies" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 glass border-b border-white/10" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="navbar-logo-link" className="flex items-center">
            <Logo className="h-9" showText />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
                className={`text-sm font-medium transition-colors hover:text-gold ${loc.pathname.startsWith(l.to) ? "text-gold" : "text-slate-200"}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                {user.role === "admin" && !user.must_change_password && (
                  <Button variant="ghost" data-testid="nav-admin-btn" onClick={() => nav("/admin")}
                    className="text-slate-200 hover:text-gold gap-2"><ShieldCheck className="h-4 w-4" />Admin</Button>
                )}
                {!user.must_change_password && (
                  <Button variant="ghost" data-testid="nav-dashboard-btn" onClick={() => nav("/account")}
                    className="text-slate-200 hover:text-gold gap-2"><LayoutDashboard className="h-4 w-4" />My Account</Button>
                )}
                <Button data-testid="nav-logout-btn" onClick={() => { logout(); nav("/"); }}
                  className="btn-gold gap-2"><LogOut className="h-4 w-4" />Sign out</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" data-testid="nav-login-btn" onClick={() => nav("/login")}
                  className="text-slate-200 hover:text-gold gap-2"><User className="h-4 w-4" />Sign in</Button>
                <Button data-testid="nav-register-btn" onClick={() => nav("/register")} className="btn-gold">Sign up</Button>
              </>
            )}
          </div>

          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-white/10 px-4 py-4 space-y-2" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-slate-200 hover:text-gold">{l.label}</Link>
          ))}
          <div className="pt-2 border-t border-white/10 space-y-2">
            {user ? (
              <>
                {user.role === "admin" && !user.must_change_password && <Button className="w-full btn-gold" onClick={() => { setOpen(false); nav("/admin"); }}>Admin</Button>}
                {!user.must_change_password && <Button className="w-full" variant="secondary" onClick={() => { setOpen(false); nav("/account"); }}>My Account</Button>}
                <Button className="w-full" variant="ghost" onClick={() => { logout(); setOpen(false); nav("/"); }}>Sign out</Button>
              </>
            ) : (
              <>
                <Button className="w-full" variant="secondary" onClick={() => { setOpen(false); nav("/login"); }}>Sign in</Button>
                <Button className="w-full btn-gold" onClick={() => { setOpen(false); nav("/register"); }}>Sign up</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
